import * as angular from 'angular';
import CheckEditor, {openCheckEditor} from "./checkEditor.controller";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import CheckListOverrideModule, {
  CheckItem, CheckList, CheckListCommon
} from "../../models/checkLists/checkListOverride.module";
import {apsCommands} from "../../shared/dto/aps.commands";
import CheckListsServiceName, {CheckListsService} from "../../services/checkLists/checkLists.service";
import UserServiceName, {UserService} from "../../services/users/user.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import UIUtilityServiceName, {EnumOption, UIUtilityService} from "../../services/utility/uiUtility.service";
import NewGuidServiceName, {NewGuidService} from "../../services/utility/newguid.service";
import {Plant} from "../../models/navigation";
import {apsCommon} from "../../shared/dto/aps.common";
import * as _ from "lodash";
import template from "./checkList.tpl.html";
import FillOutCheckListName, { openFillOutCheckList } from '../checks/fillOutCheckList.controller';
import NavigationServiceName, { NavigationService } from '../../services/navigation/navigation.service';
import { variance } from 'd3';

let requires = [CheckEditor, CheckListsServiceName, UserServiceName, AuthDataServiceName, LoaderServiceName,
  NotifyServiceName, UIUtilityServiceName, NewGuidServiceName, FillOutCheckListName, NavigationServiceName];

export interface CheckListControllerData {
  id: string;
  mode: string;
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  job: apsViewModels.JobItem;
  plants: Plant[];
  shops: apsViewModels.ShopNavigation[];
  wareClasses: apsCommon.IdAndName[];
  wareClass: apsCommon.IdAndName;
  jobs: apsViewModels.JobItem[];
  locations: apsCommon.IdAndName[];
  checkLists: CheckList[];
  checkListGroupName: string;
  departments: string[];
  pasTypes: string[];
  onAdd?: (cmd: apsCommands.AddCheckList) => void;
}

export function openEditCheckList(data: CheckListControllerData,
                                  $modal: mm.foundation.modal): angular.IPromise<{action: string, data: any}> {
  return $modal.open({
    template: template,
    controller: 'CheckListCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

interface FrequencyOffset {
  offset: number;
  description: string;
}

interface FrequencySelection {
  frequency: number;
  frequencyUnits: apsEnums.TimeUnits;
  name: string;
  offsets: FrequencyOffset[];
}

interface SignOffRole {
  id: string;
  name: string;
  failuresOnly: boolean;
}

export interface CheckListController {
  allowedRoles: string[];
  checkListForm: any;
  plants: Plant[];
  shops: apsViewModels.ShopNavigation[];
  wareClasses: apsCommon.IdAndName[];
  wareClass: apsCommon.IdAndName;
  jobs: apsViewModels.JobItem[];
  job: apsViewModels.JobItem;
  jobId: string;
  initialJobName: string;
  jobName: string;
  locations: apsCommon.IdAndName[];
  allLocationTypes: EnumOption[];
  locationTypes: EnumOption[];
  isOther(): boolean;
  roles: apsViewModels.RoleListItem[];
  availableRoles(): apsViewModels.RoleListItem[];
  roleToAdd: apsViewModels.RoleListItem;
  addAllowedRole(): void;
  removeAllowedRole(roleId: string): void;
  getRoleName(roleId: string): string;
  isNew: boolean;
  isReadOnly: boolean;
  id: string;
  title: string;
  name: string;
  description: string;
  department: string;
  pasType: string;
  topNumber: string;
  isScheduled: boolean;
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  location: apsCommon.IdAndName;
  locationType: EnumOption;
  checkPlant: Plant;
  checkShop: apsViewModels.ShopNavigation;
  checkShops: apsViewModels.ShopNavigation[];
  role: apsViewModels.RoleListItem;
  checks: CheckItem[];
  frequencySelections: FrequencySelection[];
  frequencySelection: FrequencySelection;
  frequencyOffsetSelection: FrequencyOffset;
  canBeGroup: boolean;
  isGroup: boolean;
  isGroupChanged(): void;
  canBeScheduled: boolean;
  canHaveRole: boolean;
  checkLists: CheckList[];
  availableChildLists(): CheckList[];
  childLists: CheckList[];
  childAdded(): void;
  childName: string;
  removeChild(checkList: CheckList): void;
  pasTypes: string[];
  departments: string[];

  requiresSignOff: boolean;
  signOffRoleToAdd: SignOffRole;
  availableSignOffRoles: SignOffRole[];
  signOffRoles: SignOffRole[];

  shops_filtered(): apsViewModels.ShopNavigation[];
  plantShops(): apsViewModels.ShopNavigation[];
  locationTypeChanged(): void;
  addCheckList(): void;
  saveCheckList(): void;
  cancel(): void;
  reloadChecks(): void;

  addCheck(): void;
  openCheck(check: CheckItem): void;
  deleteCheck(check: CheckItem): void;
  plantDisplayName: () => string;
  shopDisplayName: () => string;

  addSignOffRole: () => void;
  removeSignOffRole: (role: SignOffRole) => void;

  toggleMandatory(check: CheckItem): void;
  toggleDisabled(check: CheckItem): void;

  checkListGroupName: string;
  canOverrideRole(): boolean;
  overrideRoles(): apsViewModels.RoleListItem[];
  overrideRole: apsViewModels.RoleListItem;
  saveRoleOverride(): void;
  override: apsViewModels.CheckListOverrideItem;
  isShopLocation: boolean;
  checksTabActive: boolean;

  dragDropListeners: any;

  previewChecklist(): void;
  jobNameChanged(): void;
  clearJob(): void;
  frequencyChanged(): void;
  wareClassChanged(): void;
}

export default angular.module('aps.controller.checkList', requires)
  .controller('CheckListCtrl', checkListCtrl)
  .name;

function generateOffsets(count: number, unit: string, startPeriod: string, prefix?: string): FrequencyOffset[] {
  prefix = prefix || ''
  return _.range(count).map(i => {
    let plural = i > 1 ? 's' : ''
    return {
      offset: i,
      description: i === 0 ? `${prefix}at ${startPeriod}` : `${prefix}${i} ${unit}${plural} after ${startPeriod}`
    }
  });
}

function getDay(day: number) {
  switch (day) {
    case 1:
      return '1st';
    case 2:
      return '2nd';
    case 3:
      return '3rd';
    case 21:
      return '21st';
    case 22:
      return '22nd';
    case 23:
      return '23rd';
  }
  return `${day}th`;
}

function getDayDescription(day: number) {
  return `on the ${getDay(day)} of the month`;
}

function checkListCtrl(
  $modalInstance: mm.foundation.modalInstance,
  $modal: mm.foundation.modal,
  data: CheckListControllerData,
  checkListsService: CheckListsService,
  userService: UserService,
  navigationService: NavigationService,
  authDataService: AuthDataService,
  loaderService: LoaderService,
  notifyService: NotifyService,
  uiUtilityService: UIUtilityService,
  newGuidService: NewGuidService,
  $q: angular.IQService) {

  'use strict';

  var shopsAllSelection = CheckListCommon.getAllShopsItem();
  var plantsAllSelection = CheckListCommon.getAllPlantsItem();

  var vm = <CheckListController>this;

  var reOrderChecks = (checks: CheckItem[]) => {
    _.each(checks, (check: CheckItem) => {
      check.isFirst = check.isLast = false;
    });
    checks[0].isFirst = true;
    checks[checks.length - 1].isLast = true;
  };

  var loadChecks = (checks: apsViewModels.CheckItem[]) => {
    if (vm.checks.length > 0) {
      vm.checks = [];
    }

    _.sortBy(checks, 'order').forEach((check) => {
      var checkItem = CheckListOverrideModule.extendCheckItem(check);
      vm.checks.push((checkItem));
    });

    if (vm.checks.length > 0) {
      vm.canBeGroup = false;
      vm.checks[0].isFirst = true;
      vm.checks[vm.checks.length - 1].isLast = true;
    }
    vm.reloadChecks();
  };

  vm.plants = [];
  vm.shops = [];
  vm.checks = [];
  vm.wareClasses = [];
  vm.jobs = data.jobs;
  vm.job = data.job;
  if (data.job) {
    vm.jobName = data.job.name;
    vm.jobId = data.job.id;
    vm.initialJobName = vm.jobName;
  } else {
    vm.jobName = null;
    vm.jobId = null;
    vm.initialJobName = null;
  }

  var existingSignOffs = [];
  vm.signOffRoleToAdd = undefined;
  vm.availableSignOffRoles = [];
  vm.signOffRoles = [];
  vm.canBeGroup = true;
  vm.isGroup = false;
  vm.canHaveRole = true;
  vm.canBeScheduled = true;
  vm.checkLists = data.checkLists;
  vm.childLists = [];
  vm.isShopLocation = true;
  vm.checksTabActive = false;

  vm.isGroupChanged = () => {
    vm.canHaveRole = !vm.isGroup;
    if (!vm.canHaveRole) {
      vm.role = null;
      vm.signOffRoles = [];
    }
  };

  vm.childAdded = () => {
    var childList = _.find(vm.checkLists, {name: vm.childName});
    if (!childList) {
      return;
    }

    vm.childName = null;
    vm.childLists.push(childList);
  };

  vm.removeChild = (checkList) => {
    _.remove(vm.childLists, checkList);
  };

  vm.frequencyChanged = () => {
    vm.frequencyOffsetSelection = vm.frequencySelection.offsets[0];
  }

  vm.wareClassChanged = () => {
    if (vm.wareClass.id) {
      restrictToShop();
    } else {
      unrestrictLocation();
    }
  }

  vm.frequencySelections = [
    {frequencyUnits: apsEnums.TimeUnits.Minutes, frequency: 30, name: 'Every 30 minutes', offsets: [{offset: 0, description: ''}]},
    {frequencyUnits: apsEnums.TimeUnits.Hours, frequency: 1, name: 'Once per hour', offsets: [{offset: 0, description: ''}]},
    {frequencyUnits: apsEnums.TimeUnits.Hours, frequency: 2, name: 'Every 2 hours', offsets: generateOffsets(2, 'hour', 'shift start', 'starting ')},
    {frequencyUnits: apsEnums.TimeUnits.Hours, frequency: 4, name: 'Every 4 hours', offsets: generateOffsets(4, 'hour', 'shift start', 'starting ')},
    {frequencyUnits: apsEnums.TimeUnits.Shift, frequency: 1, name: 'Once per shift', offsets: generateOffsets(8, 'hour', 'shift start')},
    {frequencyUnits: apsEnums.TimeUnits.Days, frequency: 1, name: 'Once per day', offsets: generateOffsets(24, 'hour', 'the start of the first shift')},
    {frequencyUnits: apsEnums.TimeUnits.Weeks, frequency: 1, name: 'Once per week', offsets: [{
      offset: 0, description: 'on Sunday'
    }, {
      offset: 1, description: 'on Monday'
    }, {
      offset: 2, description: 'on Tuesday'
    }, {
      offset: 3, description: 'on Wednesday'
    }, {
      offset: 4, description: 'on Thursday'
    }, {
      offset: 5, description: 'on Friday'
    }, {
      offset: 6, description: 'on Saturday'
    }]},
    {frequencyUnits: apsEnums.TimeUnits.Months, frequency: 1, name: 'Once per month', offsets: _.range(0,28).map(day => {
      return {
        offset: day,
        description: getDayDescription(day+1)
      }
    })},
  ];
  vm.frequencySelection = _.first(vm.frequencySelections);
  vm.frequencyChanged();

  var promises = [];

  vm.jobNameChanged = () => {
    vm.job = _.find(vm.jobs, { name: vm.jobName });
    vm.reloadChecks();
  };

  vm.clearJob = () => {
    if (!vm.job) {
      return;
    }
    vm.job = null;
    vm.jobName = null;
    vm.reloadChecks();
  }

  vm.reloadChecks = () => {
    var plantId = vm.checkPlant.id;
    var shopId = null;
    var jobId = vm.job ? vm.job.id : null;
    if (plantId && vm.checkShop && vm.checkShop.plantId === plantId) {
      shopId = vm.checkShop.id;
    } else {
      vm.checkShop = shopsAllSelection;
    }
    CheckListOverrideModule.setOverrideValues(vm.checks, plantId, shopId, jobId, authDataService);
  };

  var saveOverride = (check: CheckItem, isMandatory: boolean, isDisabled: boolean) => {
    var plantId = vm.checkPlant.id;
    var shopId = vm.checkShop.id;
    var jobId = vm.job ? vm.job.id : null;
    var override = _.find(check.overrides, {plantId: plantId, shopId: shopId, jobId: jobId});
    checkListsService.saveCheckOverride({
      checkId: check.id,
      checkListId: vm.id,
      plantId: plantId,
      shopId: shopId,
      jobId: jobId,
      isMandatory: isMandatory,
      isDisabled: isDisabled
    }).then(() => {
      if (!override) {
        override = {
          id: _.uniqueId('checkOverride'),
          checkId: check.id,
          plantId: plantId,
          shopId: shopId,
          jobId: jobId,
          isMandatory: isMandatory,
          isDisabled: isDisabled
        };
        check.overrides.push(override);
      } else {
        override.isMandatory = isMandatory;
        override.isDisabled = isDisabled;
      }
      vm.reloadChecks();
    });
  };

  vm.toggleMandatory = (check: CheckItem) => {
    saveOverride(check, check.isMandatory, false);
  };

  vm.toggleDisabled = (check: CheckItem) => {
    saveOverride(check, false, check.isDisabled);
  };

  var loadSignOffs = () => {
    vm.signOffRoles = [];

    _.each(vm.roles, (role: apsViewModels.RoleListItem) => {
      var r = <SignOffRole> {
        id: role.id,
        name: role.name
      };

      var existing = _.find(existingSignOffs, {'roleId': role.id});
      if (existing) {
        r.failuresOnly = existing.failuresOnly;
        vm.signOffRoles.push(r);
      } else {
        r.failuresOnly = false;
        vm.availableSignOffRoles.push(r);
      }
    });
  };

  var rolesPromise = userService.getRoles().then(function (roles) {
    vm.roles = roles;
    loadSignOffs();
  });

  vm.availableRoles = () => {
    var roles = [];
    if (!vm.roles) {
      return roles;
    }

    _.forEach(vm.roles, (role: apsViewModels.RoleListItem) => {
      if (vm.role === role || _.includes(vm.allowedRoles, role.id)) {
        return;
      }

      roles.push(role);
    });
    return roles;
  };

  promises.push(rolesPromise);

  vm.plants = data.plants;
  vm.shops = data.shops;
  vm.plant = data.plant;
  vm.shop = data.shop;
  vm.wareClasses = data.wareClasses;
  vm.wareClass = data.wareClass;
  vm.checkPlant = data.plant;
  vm.checkShop = data.shop;
  vm.checkShops = data.shops;
  vm.locations = data.locations;
  vm.allLocationTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.LocationType, 1);
  vm.locationTypes = vm.allLocationTypes;
  vm.departments = data.departments;
  vm.pasTypes = data.pasTypes;

  vm.isOther = () => {
    return vm.locationType && vm.locationType.value === apsEnums.LocationType.Other;
  };

  vm.locationType = _.find(vm.allLocationTypes, {value: apsEnums.LocationType.Shop});

  var restrictToShop = () => {
    vm.locationTypes = _.filter(vm.allLocationTypes, {value: apsEnums.LocationType.Shop});
    if (vm.locationType.value !== apsEnums.LocationType.Shop) {
      vm.locationType = vm.locationTypes[0];
      vm.locationTypeChanged();
    }
  };

  var unrestrictLocation = () => {
    if ((vm.shop && vm.shop.id) || (vm.wareClass && vm.wareClass.id)) {
      return;
    }
    vm.locationTypes = vm.allLocationTypes;
  }

  var switchToEditMode = () => {
    vm.isNew = false;
    vm.checksTabActive = true;
  };

  if (data.mode === 'add') {
    vm.isNew = true;
    vm.isReadOnly = false;
    vm.name = '';
    vm.description = '';
    vm.department = '';
    vm.pasType = '';
    vm.topNumber = '';
    vm.isScheduled = false;
    vm.role = null;
    vm.allowedRoles = [];

    newGuidService.getNewGuid('checkList').then(id => vm.id = id);

    vm.requiresSignOff = false;
    vm.availableSignOffRoles = [];
    vm.signOffRoles = [];

    if ((data.shop && data.shop.id)
      || (data.wareClass && data.wareClass.id)) {
      restrictToShop();
    }

    loadSignOffs();
  } else {
    vm.isNew = false;
    vm.isReadOnly = data.mode === 'view';
    vm.id = data.id;
    vm.checkListGroupName = data.checkListGroupName;
    vm.childLists = _.filter(data.checkLists, {checkListGroupId: vm.id});

    promises.push(checkListsService.getCheckList(data.id).then((checkList) => {
      vm.name = checkList.name;
      vm.description = checkList.description;
      vm.department = checkList.department;
      vm.pasType = checkList.pasType;
      vm.topNumber = checkList.topNumber;
      vm.isScheduled = checkList.isScheduled;
      vm.allowedRoles = checkList.allowedRoles;
      if (checkList.isScheduled) {
        vm.frequencySelection = _.find(vm.frequencySelections, {
          frequency: checkList.frequency,
          frequencyUnits: checkList.frequencyUnits
        });
        vm.frequencyOffsetSelection = vm.frequencySelection
          ? _.find(vm.frequencySelection.offsets, {offset: checkList.frequencyOffset || 0})
          : null;
      }

      vm.isGroup = checkList.isGroup;
      vm.canBeGroup = (checkList.checkListGroupId === null);
      vm.canBeScheduled = vm.canBeGroup;
      vm.canHaveRole = !vm.isGroup;
      vm.isShopLocation = checkList.locationType === apsEnums.LocationType.Shop;

      if (checkList.shopId
        || checkList.wareClassId
        || _.some(checkList.checks,
          (c: apsViewModels.CheckItem) => c.entry !== apsEnums.CheckEntry.PerShop)) {
        restrictToShop();
      }

      var locationType = _.find(vm.locationTypes, {value: checkList.locationType});
      if (locationType) {
        vm.locationType = locationType;
        if (checkList.locationType === apsEnums.LocationType.Other) {
          vm.location = _.find(vm.locations, {id: checkList.locationId});
        }
      }

      rolesPromise.then(() => {
        vm.role = _.find(vm.roles, {id: checkList.roleId});
        if (vm.plant.id) {
          vm.override = _.find(checkList.overrides, {
            plantId: vm.plant.id,
            shopId: vm.shop.id
          });
          if (vm.override) {
            vm.overrideRole = _.find(vm.roles, {id: vm.override.roleId});
          } else if (!vm.shop.id) {
            vm.override = _.find(checkList.overrides, {
              plantId: vm.plant.id,
              shopId: null
            });
            if (vm.override) {
              vm.overrideRole = _.find(vm.roles, {id: vm.override.roleId});
            }
          }
        }
      });

      vm.requiresSignOff = checkList.signOffs && checkList.signOffs.length > 0;
      vm.availableSignOffRoles = [];

      if (!vm.isReadOnly) {
        if ((!checkList.plantId && vm.plant.id)
          || (!checkList.shopId && vm.shop.id)
          || (checkList.plantId && checkList.plantId !== vm.plant.id)
          || (checkList.shopId && checkList.shopId !== vm.shop.id)) {
          vm.isReadOnly = true;
        }
      }

      existingSignOffs = checkList.signOffs || [];
      vm.signOffRoles = [];
      loadSignOffs();

      loadChecks(checkList.checks);
    }));
  }

  vm.canOverrideRole = () => {
    return vm.plant.id && authDataService.isPlantAdmin(vm.plant.id);
  };

  loaderService.loadingPromise($q.all(promises));

  vm.shops_filtered = function () {
    return _.filter(vm.shops, function (shop) {
      return vm.plant && shop.plantId === vm.plant.id;
    });
  };

  vm.plantShops = () => {
    if (vm.checkPlant === plantsAllSelection) {
      return [shopsAllSelection];
    }

    if (vm.checkShops.length > 0) {
      return vm.checkShops;
    }

    return [shopsAllSelection].concat(vm.checkPlant.shops);
  };

  var addSignOffs = (command: any) => {
    var signOffs = [];
    if (vm.requiresSignOff) {
      _.each(vm.signOffRoles, (role: SignOffRole) => {
        var existing = _.find(existingSignOffs, {'roleId': role.id});
        if (existing) {
          signOffs.push(existing);
        } else {
          signOffs.push({id: '', roleId: role.id, failuresOnly: role.failuresOnly });
        }
      });
      command.signOffs = signOffs;
      command.requiresSignOff = signOffs.length > 0;
    } else {
      command.signOffs = undefined;
      command.requiresSignOff = false;
    }
  };

  function childListsInvalid() {
    return vm.isGroup && vm.childLists.length === 0;
  }

  function getChildListIds() {
    var ids = [];
    _.forEach(vm.childLists, c => ids.push({id: c.id}));
    return ids;
  }

  vm.locationTypeChanged = function () {
    if (vm.locationType.value !== apsEnums.LocationType.Other) {
      vm.location = undefined;
    }
    vm.isShopLocation = vm.locationType.value === apsEnums.LocationType.Shop;
  };

  vm.addCheckList = function () {
    if (vm.checkListForm.$invalid || childListsInvalid()) {
      return;
    }

    var command: apsCommands.AddCheckList = {
      id: vm.id,
      name: vm.name,
      description: vm.description,
      department: vm.department,
      pasType: vm.pasType,
      topNumber: vm.topNumber ? ('0000' + vm.topNumber).slice(-4) : null,
      plantId: vm.plant ? vm.plant.id : null,
      shopId: vm.shop ? vm.shop.id : null,
      roleId: vm.role ? vm.role.id : null,
      locationType: vm.locationType ? vm.locationType.value : null,
      locationId: vm.location ? vm.location.id : null,
      wareClassId: vm.wareClass.id,
      isScheduled: vm.isScheduled,
      frequency: null,
      frequencyUnits: null,
      frequencyOffset: null,
      isGroup: vm.isGroup,
      childCheckLists: getChildListIds(),
      allowedRoles: vm.allowedRoles,
      requiresSignOff: vm.requiresSignOff,
      signOffs: []
    };

    addSignOffs(command);

    if (command.isScheduled) {
      command.frequency = vm.frequencySelection.frequency;
      command.frequencyUnits = vm.frequencySelection.frequencyUnits;
      command.frequencyOffset = vm.frequencyOffsetSelection.offset;
    }

    // call the service
    loaderService.loadingPromise(
      checkListsService.addCheckList(command)
        .then(function () {
          if (data.onAdd) {
            data.onAdd(command);
          }
          if (vm.isGroup) {
            $modalInstance.close();
          } else {
            switchToEditMode();
          }
        }, notifyService.handleApiErrors));
  };

  vm.saveCheckList = function () {
    if (vm.isNew) {
      vm.addCheckList();
      return;
    }

    if (vm.checkListForm.$invalid || childListsInvalid()) {
      return;
    }

    var command: apsCommands.SaveCheckList = {
      id: vm.id,
      name: vm.name,
      description: vm.description,
      department: vm.department,
      pasType: vm.pasType,
      topNumber: vm.topNumber ? ('0000' + vm.topNumber).slice(-4) : null,
      roleId: vm.role ? vm.role.id : null,
      locationType: vm.locationType ? vm.locationType.value : null,
      locationId: vm.location ? vm.location.id : null,
      wareClassId: vm.wareClass.id,
      isScheduled: vm.isScheduled,
      frequency: null,
      frequencyUnits: null,
      frequencyOffset: null,
      isGroup: vm.isGroup,
      childCheckLists: getChildListIds(),
      allowedRoles: vm.allowedRoles,
      requiresSignOff: vm.requiresSignOff,
      signOffs: []
    };

    addSignOffs(command);

    if (command.isScheduled) {
      command.frequency = vm.frequencySelection.frequency;
      command.frequencyUnits = vm.frequencySelection.frequencyUnits;
      command.frequencyOffset = vm.frequencyOffsetSelection.offset;
    }

    // call the service
    loaderService.loadingPromise(
      checkListsService.saveCheckList(command)
        .then(function () {
          $modalInstance.close({action: 'save', data: command});
        }, notifyService.handleApiErrors));
  };

  vm.cancel = function () {
    $modalInstance.close();
  };

  var launchCheckEditor = (check?: CheckItem) => {
    var addingCheck = (!check);
    var headings = _.uniq<string>(_.map(vm.checks, 'heading')).sort();

    let result = openCheckEditor({
      checkListId: vm.id,
      checkList: vm,
      check: check,
      checks: vm.checks,
      headings: headings,
      isReadOnly: vm.isReadOnly,
      isShopCheckList: vm.locationType && vm.locationType.value === apsEnums.LocationType.Shop
    }, $modal);

    result.then(function (data: apsViewModels.CheckItem) {
      if (!data) {
        return;
      }

      if (check) {
        data.overrides = check.overrides;
      }
      var checkData = CheckListOverrideModule.extendCheckItem(data);
      if (addingCheck) {
        checkData.order = (vm.checks.length > 0) ?
          _.max<number>(_.map(vm.checks, 'order')) + 1 : 0;
        vm.checks.push(checkData); // insert at the bottom
      } else {

        var index = vm.checks.indexOf(check);
        if (index > -1) {
          vm.checks[index] = _.assign<CheckItem>(checkData, {
            isFirst: check.isFirst,
            isLast: check.isLast
          });
        }
      }

      if (data.entry !== apsEnums.CheckEntry.PerShop) {
        restrictToShop();
      }

      reOrderChecks(vm.checks);
      vm.reloadChecks();
    });

  };

  vm.addCheck = () => {
    launchCheckEditor();
  };

  vm.openCheck = (check: CheckItem) => {
    launchCheckEditor(check);
  };

  vm.deleteCheck = (check: CheckItem) => {
    uiUtilityService.confirm('Delete the check: ' + check.name).then((result) => {
      if (result) {
        loaderService.loadingPromise(
          checkListsService.deleteCheck({
              id: check.id,
              checkListId: vm.id
            })
            .then(() => {
               _.remove(vm.checks, check);

               reOrderChecks(vm.checks);

            }, notifyService.handleApiErrors)
        );
      }
    });
  }

  vm.previewChecklist = () => {
    navigationService.getNavigation().then(nav => {
      var plantId = nav.selectedPlant.id;
      var shopId = nav.selectedShop.id;
      if (vm.checkShop && vm.checkShop.id) {
        shopId = vm.checkShop.id;
        plantId = vm.checkShop.plantId;
      }
      else if (vm.checkPlant && vm.checkPlant.id) {
        var plant = nav.plants().filter(x => x.id === vm.checkPlant.id)[0];
        var shop = plant.shops[0];
        shopId = shop.id;
        plantId = plant.id;
      }

      openFillOutCheckList({
        checkListId: vm.id,
        plantId: plantId,
        tankId: null,
        shopId: shopId,
        instanceId: null,
        plantLocationId: null,
        preview: true
      }, $modal);        
    })
  };

  vm.shopDisplayName = () => {
    return vm.shop ? vm.shop.name : 'All';
  };

  vm.plantDisplayName = () => {
    return vm.plant ? vm.plant.name : 'All';
  };

  vm.addSignOffRole = () => {
    if (vm.signOffRoleToAdd) {
      _.remove(vm.availableSignOffRoles, {'id': vm.signOffRoleToAdd.id});
      vm.signOffRoles.push(vm.signOffRoleToAdd);
    }
    vm.signOffRoleToAdd = undefined;
  };

  vm.removeSignOffRole = (role: SignOffRole) => {
    _.remove(vm.signOffRoles, {'id': role.id});
    vm.availableSignOffRoles.push(role);
  };

  vm.addAllowedRole = () => {
    if (!vm.roleToAdd) {
      return;
    }

    vm.allowedRoles.push(vm.roleToAdd.id);
  };

  vm.removeAllowedRole = (roleId: string) => {
    _.remove(vm.allowedRoles, r => r === roleId);
  };

  vm.getRoleName = (roleId: string) => {
    var role = _.find(vm.roles, {id: roleId});
    if (role) {
      return role.name;
    }
    return '';
  };

  vm.availableChildLists = () => {
    return _.filter(vm.checkLists, (c: CheckList) => {
      return !_.some(vm.childLists, {id: c.id});
    });
  };

  vm.overrideRoles = () => {
    if (vm.role) {
      return _.filter(vm.roles, r => r.id !== vm.role.id);
    }
    return [];
  };

  vm.saveRoleOverride = () => {
    if ((vm.override && vm.overrideRole && vm.override.roleId === vm.overrideRole.id)
      || (!vm.override && !vm.overrideRole)) {
      vm.cancel();
    }

    var cmd = {
      checkListId: vm.id,
      plantId: vm.plant.id,
      shopId: vm.shop.id,
      jobId: vm.jobId,
      roleId: vm.overrideRole ? vm.overrideRole.id : null,
      isMandatory: vm.override ? vm.override.isMandatory : false,
      isDisabled: vm.override ? vm.override.isDisabled : false
    };
    loaderService.loadingPromise(
      checkListsService.saveOverride(cmd).then(() => {
        $modalInstance.close({action: 'override', data: cmd});
      }, notifyService.handleApiErrors));
  };

  vm.dragDropListeners = {
    orderChanged: function(event) {
      if (vm.isReadOnly) {
        return;
      }

      loaderService.loadingPromise(
        checkListsService.reorderCheck({
          checkListId: vm.id,
          fromIndex: event.source.index,
          toIndex: event.dest.index}));
    }
  };
}


// WEBPACK FOOTER //
// ./src/app/controllers/checkLists/checkList.controller.ts